<template>
	<div>
		<ValidationObserver
			ref="registerForm"
			v-slot="{ invalid, errors }"
			class="sign-up"
			tag="form"
			@submit.prevent="onRegister"
			novalidate
		>
			<h2 v-html="$t('login.registerTitle')" v-if="!hideTitle"/>

			<GeneralMessage
				v-if="registerResponse.message"
				:message="registerResponse.message"
				:type="registerResponse.type"
				@close="registerResponse.message = ''"
			/>

			<ValidationProvider
				class="form-row__wrapper"
				name="firstname"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="text"
					:placeholder="$t('forms.labels.firstname')"
					:label="$t('forms.labels.firstname')"
					v-model="registerForm.firstName"
					:error="invalid && validated"
					:error-message="errors[0]"
					:disabled="disabled"
					required
				/>
			</ValidationProvider>

			<ValidationProvider
				class="form-row__wrapper"
				name="lastname"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="text"
					:placeholder="$t('forms.labels.lastname')"
					:label="$t('forms.labels.lastname')"
					v-model="registerForm.lastName"
					:error="invalid && validated"
					:error-message="errors[0]"
					:disabled="disabled"
					required
				/>
			</ValidationProvider>

			<!-- EMAIL -->
			<ValidationProvider
				class="form-row__wrapper"
				name="email"
				rules="required|email"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				ref="registerEmail"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
			>
				<FormInput
					type="email"
					:placeholder="$t('forms.labels.email')"
					:label="$t('forms.labels.email')"
					v-model="registerForm.email"
					:error="invalid && validated"
					:error-message="errors[0]"
					:disabled="disabled"
					:hint="$t('login.registerEmailHint')"
					required
				/>
			</ValidationProvider>

			<div class="form-row__wrapper">
				<FormInputDate
					:placeholder="$t('forms.labels.birthday')"
					:label="$t('forms.labels.birthday')"
					:labelOption="$t('forms.labels.optional')"
					v-model="registerForm.birthday"
					max-date="today"
				/>
			</div>

			<!-- PASSWORD -->
			<ValidationProvider
				class="form-row__wrapper"
				name="password"
				rules="required|complexpw"
				vid="password"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), complexpw: $t('forms.errors.password') }"
			>
				<FormInput
					type="password"
					:placeholder="$t('forms.labels.password')"
					:label="$t('forms.labels.password')"
					v-model="registerForm.password"
					:error="invalid && validated"
					:error-message="errors[0]"
					:disabled="disabled"
					required
				/>
			</ValidationProvider>

			<!-- PASSWORD CONFIRM -->
			<ValidationProvider
				class="form-row__wrapper"
				name="confirm"
				vid="confirm"
				rules="required|confirmed:password"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), confirmed: $t('forms.errors.passwordMatch') }"
			>
				<FormInput
					type="password"
					:placeholder="$t('forms.labels.passwordRepeat')"
					:label="$t('forms.labels.passwordRepeat')"
					v-model="registerForm.confirm"
					:error="invalid && validated"
					:error-message="errors[0]"
					:disabled="disabled"
					required
				/>
			</ValidationProvider>

			<ValidationProvider
				v-if="showPhone"
				class="form-row__wrapper"
				name="phone"
				rules="required|phone_int"
				v-slot="{ errors, invalid, validated }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone_int: $t('forms.errors.phone') }"
			>
				<FormInput
					type="tel"
					:placeholder="$t('forms.labels.phone')"
					:label="$t('forms.labels.phone')"
					v-model="registerForm.phoneNumber"
					:error="errors.length > 0"
					:error-message="errors[0]"
					:disabled="!!isLoggedIn"
					:hint="$t('login.phoneHint')"
					required
				/>
			</ValidationProvider>

			<ValidationProvider
				:rules="{ required: { allowFalse: false } }"
				name="checkTerms"
				tag="div"
				v-slot="{ invalid, errors, validated }"
				:customMessages="{ required: $t('login.errorTerms') }"
			>
				<FormInputCheckbox
					name="terms"
					value="checkTerms"
					v-model="registerForm.checkTerms"
					:error="errors.length > 0"
					:error-message="errors[0]"
					required
					immediate
					:disabled="disabled"
				>
					<span v-html="$t('login.termsLabel')"/>
				</FormInputCheckbox>
			</ValidationProvider>

			<Button
				type="submit"
				level="primary"
				:text="$t('login.registerButton')"
				:disabled="disabled"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import GenericMixin from '@/mixins/GenericVueMixin';
import OverlayMixin from '@/mixins/OverlayVueMixin';
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputDate from "@/components/FormInputDate"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import { loadRecaptcha, getRecaptchaResponse } from '@/helpers/recaptchaHelper'

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputCheckbox,
		FormInputDate,
		Button,
		GeneralMessage,
	},

	props: {
		hideTitle: Boolean,
		phone: {
			type: Boolean,
			default: false,
		},
		confirm: {
			type: String,
			default: '',
		}
	},
	data() {
		return {
			registerForm: {
				firstName: "",
				lastName: "",
				email: "",
				birthday: '',
				password: "",
				confirm: "",
				checkTerms: false,
				phoneNumber: "",
				userGroup: this.$root.options.userGroup,
				userCoupon: this.$root.options.userCoupon,
				confirmUrl: this.$root.options.confirmUrl || this.confirm,
			},
			registerResponse: {
				type: 'info',
				message: '',
			},
			showPhone: this.$root.options.showPhone ?? this.phone,
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn"
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		disabled() {
			return !!this.isLoggedIn || this.loading;
		},
	},
	methods: {
		async onRegister() {
			const isValid = await this.$refs.registerForm.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					const recaptchaResponse = await getRecaptchaResponse();
					const response = await this.$store.dispatch('register', {
						...this.registerForm,
						recaptchaResponse
					})
					if (response.redirect && response.redirect.key) {
						return this.$store.dispatch('redirect', {
							name: response.redirect.key,
							params: response.redirect.params || {},
						})
					}
					this.resetForm();
					this.registerResponse.message = this.$t('login.successRegister');
					this.registerResponse.type = 'success';
					this.$emit('registered');
				} catch(err) {
					let message = this.$t('forms.errors.generic')
					const data = err?.response?.data;

					if (data) {
						if (data.redirect && data.redirect.key) {
							return this.$store.dispatch('redirect', {
								name: data.redirect.key,
								params: data.redirect.params || {},
							})
						}

						if (data.errors && data.errors.email) {
							message = data.errors.email[0]
						} else {
							message = data.message
						}
					}

					this.registerResponse.message = message;
					this.registerResponse.type = 'error';
				}
				this.loading = false;
			}
		},
		resetForm(form, inputs = true) {
			try {
				if (!form || form === 'register') {
					if (inputs) {
						this.registerForm.firstName = '';
						this.registerForm.lastName = '';
						this.registerForm.email = '';
						this.registerForm.birthday = '';
						this.registerForm.password = '';
						this.registerForm.confirm = '';
						this.registerForm.checkTerms = false;
						this.registerForm.phoneNumber = '';
					}
					this.registerResponse.message = '';
					this.$refs.registerForm.reset();
				}
				if (!form) {
					this.loading = false;
					this.forgotPassword = false;
				}
				if (inputs) {
					document.activeElement.blur();
				}
			}
			catch(err) {}
		},
	},
	async created() {
		await loadRecaptcha()
	}
}
</script>

<style>

</style>
